/**
 * SoloRollHistoryOverlay — the Solo Grind roll ledger. A bottom sheet listing
 * every throw of the session (newest first): the bet chip, the authoritative
 * dice, the outcome, the net movement, and balance before → after. Opened by
 * tapping the top-right session net ticker.
 *
 * Why it exists: solo dice are client-trusted (balance-audit decision A1), so
 * when a player disputes a payout ("my 6 didn't pay in chuck-a-luck") the
 * server-recorded row is the receipt — it shows exactly which dice settled and
 * what they paid. Chuck-a-luck rows highlight the dice matching the bet face.
 *
 * Pure state consumer: entries arrive on customState.rollHistory via the
 * unified game:update (fed from applyPerUpdateBookkeeping), so the sheet is
 * correct after resume / late rejoin with zero extra events.
 */

import type { SoloRollHistoryEntry } from '@sdl/shared';
import { parseChuckFace } from '@sdl/shared';
import { getSidebetIconUrl } from './sidebetIcon';
import { formatCompact } from './SoloStacksCounter';

const STYLE_ID = 'solo-roll-history-styles';

class SoloRollHistoryOverlayImpl {
  private root: HTMLElement | null = null;
  private listEl: HTMLElement | null = null;
  private entries: SoloRollHistoryEntry[] = [];
  private openFlag = false;

  private ensure(): void {
    if (this.root) return;
    this.injectStyles();
    const root = document.createElement('div');
    root.className = 'solo-history-host';
    root.innerHTML = `
      <div class="solo-history-backdrop"></div>
      <div class="solo-history-sheet">
        <div class="solo-history-header">
          <span class="solo-history-title">ROLL HISTORY</span>
          <button class="solo-history-close" aria-label="Close">✕</button>
        </div>
        <div class="solo-history-list"></div>
      </div>`;
    document.body.appendChild(root);
    this.root = root;
    this.listEl = root.querySelector('.solo-history-list');
    root.querySelector('.solo-history-backdrop')?.addEventListener('click', () => this.close());
    root.querySelector('.solo-history-close')?.addEventListener('click', () => this.close());
  }

  /** Feed the authoritative ledger from customState.rollHistory (every update). */
  setEntries(entries: SoloRollHistoryEntry[]): void {
    this.entries = entries;
    if (this.openFlag) this.render();
  }

  open(): void {
    this.ensure();
    this.render();
    const root = this.root!;
    root.classList.add('mounted');
    void root.offsetHeight; // commit hidden base before the slide-in (no first-frame flash)
    root.classList.add('visible');
    this.openFlag = true;
  }

  close(): void {
    this.openFlag = false;
    this.root?.classList.remove('visible');
    // Unmount after the slide-out so the backdrop doesn't eat a frame of taps.
    window.setTimeout(() => {
      if (!this.openFlag) this.root?.classList.remove('mounted');
    }, 240);
  }

  /** Session teardown: drop the old session's rows so the next solo run starts clean. */
  clear(): void {
    this.entries = [];
    if (this.listEl) this.listEl.innerHTML = '';
  }

  private render(): void {
    if (!this.listEl) return;
    if (this.entries.length === 0) {
      this.listEl.innerHTML = `<div class="solo-history-empty">No rolls yet. Place a bet and shoot.</div>`;
      return;
    }
    // Newest first. Full rebuild is fine here: the sheet is a static, on-demand
    // ledger — nothing animates in-flight inside it.
    const rows = [...this.entries].reverse().map((e) => this.rowHtml(e)).join('');
    this.listEl.innerHTML = rows;
  }

  private rowHtml(e: SoloRollHistoryEntry): string {
    const chip = getSidebetIconUrl(e.betType, 'small');
    const face = e.mode === 'chuckaluck' ? parseChuckFace(e.betType) : null;
    const dice = e.dice
      .map((d) => `<span class="solo-history-die${face !== null && d === face ? ' match' : ''}">${d}</span>`)
      .join('');
    const total = e.mode === 'hilo'
      ? `<span class="solo-history-total">= ${e.dice.reduce((a, b) => a + b, 0)}</span>`
      : '';

    let tag: string;
    let tagClass: string;
    let net: string;
    let netClass: string;
    if (e.outcome === 'reroll') {
      tag = 'ROLL AGAIN';
      tagClass = 'reroll';
      net = 'BET RIDES';
      netClass = 'reroll';
    } else if (e.outcome === 'push') {
      tag = 'PUSH';
      tagClass = 'push';
      net = '±0';
      netClass = 'push';
    } else {
      const delta = e.balanceAfter - e.balanceBefore;
      const win = e.outcome === 'win';
      tag = win ? 'WIN' : 'LOSS';
      tagClass = win ? 'win' : 'loss';
      net = `${delta >= 0 ? '+' : '-'}${formatCompact(Math.abs(delta))}`;
      netClass = win ? 'win' : 'loss';
    }

    const balances = e.outcome === 'reroll'
      ? `Bet ${formatCompact(e.stake)} stays live`
      : `Bet ${formatCompact(e.stake)} · ${formatCompact(e.balanceBefore)} → ${formatCompact(e.balanceAfter)}`;

    return `
      <div class="solo-history-row">
        <img class="solo-history-chip" src="${chip}" alt="">
        <div class="solo-history-mid">
          <div class="solo-history-diceline">
            ${dice}${total}
            <span class="solo-history-tag ${tagClass}">${tag}</span>
          </div>
          <div class="solo-history-balances">${balances}</div>
        </div>
        <span class="solo-history-net ${netClass}">${net}</span>
      </div>`;
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .solo-history-host {
        position: fixed; inset: 0; z-index: 700;
        display: none;
      }
      .solo-history-host.mounted { display: block; }

      .solo-history-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.55);
        opacity: 0; transition: opacity 220ms ease;
      }
      .solo-history-host.visible .solo-history-backdrop { opacity: 1; }

      .solo-history-sheet {
        position: absolute; left: 0; right: 0; bottom: 0;
        max-height: 72vh;
        display: flex; flex-direction: column;
        background: linear-gradient(180deg, rgba(24,24,30,0.98), rgba(12,12,16,0.98));
        border-top: 2px solid rgba(0,229,255,0.35);
        border-radius: 18px 18px 0 0;
        padding-bottom: env(safe-area-inset-bottom, 0px);
        transform: translateY(100%);
        transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      .solo-history-host.visible .solo-history-sheet { transform: translateY(0); }

      .solo-history-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 16px 10px;
        border-bottom: 1px solid rgba(255,255,255,0.08);
      }
      .solo-history-title {
        font-family: 'Bebas Neue', Impact, sans-serif;
        font-size: 22px; letter-spacing: 1.5px; color: #EAEAF0;
      }
      .solo-history-close {
        background: rgba(255,255,255,0.08);
        border: 1px solid rgba(255,255,255,0.2);
        color: rgba(255,255,255,0.85);
        width: 30px; height: 30px; border-radius: 50%;
        font-size: 14px; line-height: 1; cursor: pointer;
      }
      .solo-history-close:active { background: rgba(255,255,255,0.16); }

      .solo-history-list {
        overflow-y: auto; -webkit-overflow-scrolling: touch;
        padding: 6px 12px 14px;
      }
      .solo-history-empty {
        padding: 34px 16px; text-align: center;
        font-family: 'Rajdhani', sans-serif; font-size: 15px; font-weight: 500;
        color: rgba(255,255,255,0.8);
      }

      .solo-history-row {
        display: flex; align-items: center; gap: 10px;
        padding: 9px 6px;
        border-bottom: 1px solid rgba(255,255,255,0.06);
      }
      .solo-history-row:last-child { border-bottom: none; }
      .solo-history-chip { width: 30px; height: 30px; flex: 0 0 auto; }
      .solo-history-mid { flex: 1 1 auto; min-width: 0; }

      .solo-history-diceline { display: flex; align-items: center; gap: 5px; }
      .solo-history-die {
        width: 22px; height: 22px; border-radius: 5px;
        display: inline-flex; align-items: center; justify-content: center;
        background: #EDEDF2; color: #16161C;
        font-family: 'Bebas Neue', Impact, sans-serif; font-size: 15px;
        border: 2px solid transparent;
      }
      .solo-history-die.match {
        border-color: #7C5CFF;
        box-shadow: 0 0 7px rgba(124,92,255,0.7);
      }
      .solo-history-total {
        font-family: 'Rajdhani', sans-serif; font-size: 13px; font-weight: 700;
        color: rgba(255,255,255,0.8);
      }
      .solo-history-tag {
        margin-left: 4px; padding: 1px 7px; border-radius: 9px;
        font-family: 'Rajdhani', sans-serif; font-size: 11px; font-weight: 700;
        letter-spacing: 0.8px;
      }
      .solo-history-tag.win    { background: rgba(0,255,136,0.16); color: #38F5A0; }
      .solo-history-tag.loss   { background: rgba(255,51,102,0.16); color: #FF5A7A; }
      .solo-history-tag.push   { background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.85); }
      .solo-history-tag.reroll { background: rgba(0,229,255,0.14); color: #4DE8FF; }

      .solo-history-balances {
        margin-top: 2px;
        font-family: 'Rajdhani', sans-serif; font-size: 13px; font-weight: 600;
        color: rgba(255,255,255,0.78);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }

      .solo-history-net {
        flex: 0 0 auto;
        font-family: 'Bebas Neue', Impact, sans-serif; font-size: 19px; letter-spacing: 0.5px;
      }
      .solo-history-net.win    { color: #38F5A0; }
      .solo-history-net.loss   { color: #FF5A7A; }
      .solo-history-net.push   { color: rgba(255,255,255,0.85); }
      .solo-history-net.reroll { color: #4DE8FF; font-size: 13px; font-family: 'Rajdhani', sans-serif; font-weight: 700; }
    `;
    document.head.appendChild(style);
  }
}

/** Always-mounted singleton (created on first open). */
export const soloRollHistoryOverlay = new SoloRollHistoryOverlayImpl();
